import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Recipe } from '../types/mealPlan';
import { RecipeDetailsView } from '../components/Recipe/RecipeDetailsView';
import { Text, Paper, Stack, Center, Loader, Group, Button } from '@mantine/core';
import { MobileOptimized } from '../components/Layout/MobileOptimized';
import { updateCatalogRecipe } from '../services/adminCatalogService';
import {
  CategorizationMenu,
  categoryLabel,
  deriveSlots,
} from '../components/Admin/CategorizationMenu';

export const CatalogRecipeDetailsPage = () => {
  const { recipeId } = useParams<{ recipeId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading || !user || !recipeId) return;

    const fetchCatalogRecipe = async () => {
      try {
        setLoading(true);
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/admin/catalog-recipes/${recipeId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) {
          throw new Error(response.status === 404 ? 'Catalog recipe not found' : 'Failed to load recipe');
        }

        const data = await response.json();
        setRecipe(data.recipe);
      } catch (err: any) {
        setError(err.message || 'Failed to load catalog recipe');
      } finally {
        setLoading(false);
      }
    };

    fetchCatalogRecipe();
  }, [recipeId, user, authLoading]);

  const handleSaveRecipe = useCallback(async (updatedRecipe: Partial<Recipe>) => {
    if (!user || !recipeId) return;
    await updateCatalogRecipe(user, recipeId, updatedRecipe);
    setRecipe(prev => prev ? { ...prev, ...updatedRecipe } : prev);
  }, [user, recipeId]);

  if (loading || authLoading) {
    return (
      <MobileOptimized>
        <Center p="xl">
          <Loader size="sm" />
        </Center>
      </MobileOptimized>
    );
  }

  if (error || !recipe) {
    return (
      <MobileOptimized>
        <Paper p="xl" withBorder>
          <Stack align="center" gap="md">
            <Text c="red">{error || 'Recipe not found'}</Text>
          </Stack>
        </Paper>
      </MobileOptimized>
    );
  }

  const effectiveSlots = recipe.slots ?? deriveSlots(recipe.category);
  const servesLine =
    (['breakfast', 'lunch', 'dinner'] as const)
      .filter((slot) => effectiveSlots[slot])
      .map((slot) => slot[0].toUpperCase() + slot.slice(1))
      .join(', ') || 'Nothing (component)';

  return (
    <>
      <title>{`${recipe.name} - Catalog | Meals You Love`}</title>
      {/* Curation bar: taxonomy + serving slots, admin-only surface so the
          control never leaks into user-facing recipe views */}
      <MobileOptimized>
        <Paper withBorder radius="lg" p="sm" mb="sm">
          <Group justify="space-between" wrap="wrap">
            <Text size="sm" c="dimmed">
              Serves: {servesLine}
            </Text>
            <CategorizationMenu
              category={recipe.category}
              slots={recipe.slots}
              onChangeCategory={(category) => handleSaveRecipe({ category })}
              onChangeSlots={(slots) => handleSaveRecipe({ slots })}
              target={
                <Button variant="default" size="xs">
                  {categoryLabel(recipe.category)}
                </Button>
              }
            />
          </Group>
        </Paper>
      </MobileOptimized>
      <RecipeDetailsView
        recipe={recipe}
        onBack={() => navigate(-1)}
        onSaveRecipe={handleSaveRecipe}
        canEdit={true}
        isOwner={true}
        isCatalogView={true}
      />
    </>
  );
};
